/**
 * 서식별 정형 항목 정의. 대기업 결재 기안문처럼 서식마다 필요한 칸(연차 기간·일수, 구매 품목 등)을 채워 넣는다.
 * 값은 {@code formData} 객체(키→값)로 모아 백엔드에 JSON 으로 저장하고, 그대로 문서·출력에 쓴다.
 */

import { groupDigits } from '@/lib/money';
import { getHolidayName } from '@/lib/utils/holidays';
import { OVERSEAS_BUDGET_FORM_KEY, budgetAmount, normalizeBudget, type OverseasBudget } from '@/pages/approval/overseasBudget';

export type FieldType = 'text' | 'number' | 'date' | 'textarea' | 'select' | 'rows' | 'items' | 'budget';

/**
 * 품목 표의 한 줄 — 잡자재 신청(DOC-139 §3.2). 「품목 · 규격 · 수량 · 단위 · 단가」 와 몰의 상품.
 *
 * <p>금액은 저장하지 않는다. 화면과 서버가 같은 산식(수량 × 단가)으로 세고, 합계가 결재 금액이다.
 * {@code provider} · {@code itemCode} · {@code productUrl} 은 잡자재 대장의 [상품 찾기] 에서 담은 줄만 갖는다 —
 * 대장이 이 값으로 빠른주문 목록을 만든다.
 */
export type MaterialItem = {
  name: string;
  spec: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  note: string;
  provider?: string;
  itemCode?: string;
  productUrl?: string;
};

export const EMPTY_MATERIAL_ITEM: MaterialItem = { name: '', spec: '', quantity: '', unit: '', unitPrice: '', note: '' };

/**
 * 항목 표의 <b>하위 줄</b> — 원본 양식의 「1)」 「2)」 (사용자 제공 협조전 2026-09-09).
 *
 * <p><b>번호를 저장하지 않는다.</b> 화면과 문서가 그릴 때 순서대로 매긴다 — 값으로 넣어 두면
 * 2번을 지운 순간 「1) 3)」이 남고, 그걸 바로잡는 단추를 또 만들게 된다.
 *
 * <p>비고가 <b>줄마다</b> 붙는다. 원본에서 「3. 현장 요청사항」의 두 줄에 각각 「(17일 도착)」
 * 「(24일 도착)」이 적혀 있다 — 비고는 큰 항목의 성질이 아니라 그 한 줄의 성질이다.
 *
 * <p>기한도 줄마다다(사용자 지시 2026-10-02 「내용, 기한, 비고」). 자유 글이다 — 「10/17 도착」처럼
 * 날짜가 아닌 말로 적는 일이 실제로 있어 날짜 칸으로 못 박지 않는다. 옛 문서에는 이 칸이 없다.
 */
export type RowItem = { content: string; due: string; note: string };

/** 빈 줄 — 표를 세울 때와 [항목 추가]가 같은 모양을 쓴다. */
export const EMPTY_ROW: RowItem = { content: '', due: '', note: '' };

export type FieldDef = {
  key: string;
  label: string;
  type: FieldType;
  /**
   * 글 칸을 <b>목록에서 고르게</b> 한다. {@code qualification} 은 자격증명 — 수당 판정과 같은 국가자격
   * 목록에서 검색해 고르고, 목록에 없는 이름은 적은 대로 쓴다(사용자 지시 2026-10-28).
   */
  pick?: 'qualification';
  options?: string[];
  placeholder?: string;
  half?: boolean;
  /**
   * 이 칸을 <b>언제</b> 보일지. 없으면 언제나 보인다.
   *
   * <p>「주말·공휴일도 차감할까」 는 그 기간에 쉬는 날이 걸릴 때만 뜻이 있는 물음이다. 늘 세워
   * 두면 평일만 고른 사람에게 <b>대답할 필요 없는 칸</b>이 하나 늘고, 그런 칸은 대충 넘겨진다.
   */
  showWhen?: (formData: Record<string, unknown>) => boolean;
  /**
   * 화면에 <b>그리지 않는</b> 칸. 값은 그대로 저장되고 문서에도 나온다.
   *
   * <p>사람이 칠 값이 아닌 것들이다 — 선행 문서의 id, 첨부 파일 키. 텍스트 칸으로 세워 두면
   * 담당자가 UUID 를 손으로 옮겨 적게 되고, 한 글자만 틀려도 대장은 <b>다른 줄</b>에 붙는다.
   */
  hidden?: boolean;
  /**
   * <b>옛 문서에서만</b> 보이는 칸 — 품목 표가 생기기 전의 한 품목 칸들(잡자재 · DOC-139).
   *
   * <p>새 기안에는 세우지 않고, 그 칸에 값이 적힌 옛 문서를 열 때만 종이에 그린다. 지워 버리면 옛 문서의
   * 품목·수량이 결재함에서 사라진다.
   */
  legacy?: boolean;
};

/**
 * 이 서식이 <b>어느 기안을 이어받는가</b> (사용자 지시 2026-08-27).
 *
 * <p>교육 수강 신청을 내고, 교육을 듣고, 그 영수증으로 교육비 정산을 낸다 — 뒤의 기안은 앞의
 * 기안 <b>위에</b> 선다. 서버는 그 연결을 이미 쓰고 있었다({@code TechEducationHandlers} 가
 * {@code applyDocumentId} 로 대장 줄을 찾는다). 없던 것은 <b>고르는 화면</b>뿐이라, 담당자는
 * 문서 id 를 손으로 옮겨 적어야 했다.
 *
 * @property key       선행 문서 id 를 담을 정형 항목의 키
 * @property label     사람이 읽는 이름 — 「수강 신청 문서」
 * @property fromForms 고를 수 있는 선행 서식. 아무 문서나 이어받게 두면 대장이 엉뚱한 줄에 붙는다
 */
export type FormLink = {
  key: string;
  label: string;
  fromForms: string[];
  hint?: string;
};

export type FormSchema = {
  fields: FieldDef[];
  /** 이 서식이 이어받는 선행 기안. 있으면 기안 화면에 「고르기」 칸이 선다. */
  linkTo?: FormLink;
  /** 연차처럼 잔여를 보여줄지. */
  showLeaveBalance?: boolean;
  /**
   * 문서 머리의 <b>「수 신」 줄</b>을 채우는 칸의 열쇠 (사용자 지시 2026-09-09 · 협조전).
   *
   * <p>사내 품의는 받는 이가 없어 그 줄이 늘 「내부품의」였다. 협조전은 <b>받는 부서가 문서의
   * 절반</b>이라 그 자리가 비면 문서가 성립하지 않는다.
   *
   * <p>여기 지목된 칸은 <b>「- 아래 -」 표에 다시 그리지 않는다</b>({@link displayFields}).
   * 머리에 이미 선 값을 아래에 한 번 더 세우면, 종이 한 장에 같은 부서 이름이 두 번 적힌다.
   */
  recipientKey?: string;
  /**
   * 자유 서술 「내용」 칸을 감출지.
   *
   * <p>서식 항목이 문서의 전부인 서식이 있다. 연차 사용이 그렇다 — 종류·기간·일수·사유를 적고 나면
   * 내용 칸에 다시 쓸 것이 없는데도 두 칸을 채워야 했고, 결국 「사유」 와 같은 말이 두 번 적혔다
   * (사용자 지시 2026-08-26 · 「요즘 회사는 연차가 자유로워야 한다」).
   *
   * <p>같은 이유로 <b>신청·정산 서식 대부분</b>이 이 칸을 감춘다(2026-08-27) — 자격증 등록·교육
   * 수강·숙소·귀향여비·경조금·정산류. 적을 것은 정형 항목이 다 받고, 덧붙일 말은 <b>비고</b> 칸이
   * 받는다. 자유 서술이 필요한 것은 일반기안(FREE)과 품의·출장처럼 <b>글이 곧 내용인</b> 서식이다.
   */
  hideBody?: boolean;
};

/**
 * 휴가 종류 (사용자 지시 2026-08-27 — 「법적으로 쓸 만한 것 다」).
 *
 * <p>앞의 셋만 <b>연차에서 깎인다</b>({@link deductsAnnual}). 나머지는 법이 따로 주는 휴가이거나
 * (예비군·출산·가족돌봄…) 별도로 쌓아 둔 것을 쓰는 것이라(대체휴무·보상휴가), 연차 잔여를 건드리면
 * 안 된다 — 예비군 다녀온 사람의 연차가 이틀 줄어 있는 것이 지금까지 일어날 수 있던 일이다.
 *
 * <p>근거: 근로기준법 60조(연차)·73조(보건)·74조(출산전후·유산사산)·57조(보상휴가),
 * 남녀고용평등법 18조의2(배우자 출산)·18조의3(난임)·22조의2(가족돌봄), 예비군법·민방위기본법.
 * 경조사·병가·포상은 법이 아니라 <b>회사가 정하는</b> 휴가라 사규를 따른다.
 *
 * <p>교육휴가는 기술인 법정교육(건설기술진흥법 20조)에 다녀오는 날이다(사용자 지시 2026-10-20, DOC-121).
 * 이름에 「연차」·「반차」가 없어 화면({@link deductsAnnual})과 서버(LeaveLedgerService.deducts) 모두
 * 깎지 않는다 — 이름을 바꿀 때 두 글자를 넣지 마라.
 */
export const LEAVE_TYPES = [
  '연차',
  '오전반차',
  '오후반차',
  '대체휴무',
  '보상휴가',
  '경조사',
  '병가',
  '공가',
  '교육휴가',
  '예비군훈련',
  '민방위훈련',
  '출산전후휴가',
  '배우자 출산휴가',
  '유산·사산휴가',
  '난임치료휴가',
  '가족돌봄휴가',
  '보건휴가',
  '포상휴가',
  '기타휴무',
];

/** 일요일·법정공휴일인가 — 근무 시간에 8 을 채우는 날. 토요일은 채우지 않고 사람이 적는다. */
function isSundayOrHoliday(value: unknown): boolean {
  if (typeof value !== 'string' || !value) return false;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  return date.getDay() === 0 || !!getHolidayName(date);
}

/**
 * 근무일을 일요일·법정공휴일로 고르면 근무 시간을 8 로 채운다 (사용자 지시 2026-10-14). 사람이 다른
 * 값을 적어 두었으면 덮지 않는다 — 비어 있거나 이미 8 일 때만 채운다. 토요일은 채우지 않는다.
 */
export function withOvertimeHours(data: Record<string, unknown>): Record<string, unknown> {
  const hours = String(data.hours ?? '');
  if (!isSundayOrHoliday(data.workDate) || (hours !== '' && hours !== '8')) return data;
  return { ...data, hours: '8' };
}

/** 대체휴무. 하루 단위로 쉬고, 쉬는 날마다 휴일근무 하나를 고른다({@code holidayWorkIds}). */
export const SUBSTITUTE_LEAVE = '대체휴무';

/** 연차 잔여를 깎는 종류인가. 연차와 반차뿐이다 — 나머지는 기록만 남고 잔여는 그대로다. */
export function deductsAnnual(leaveType: string): boolean {
  return leaveType === '' || leaveType === '연차' || leaveType.includes('반차');
}

/**
 * 협조전 — 부서 사이 <b>공문</b>인 서식.
 *
 * <p>이 서식만 다르게 다루는 곳이 여럿이라 이름을 한 곳에 둔다: 대장 탭이 이 값으로 목록을
 * 받고({@code useFormLedger}), 기안문이 이 값으로 <b>결재란을 두 칸으로</b> 그리며
 * ({@code escalatesToPaper}), 서버는 이 값으로 <b>받은 팀 전체</b>에게 열람을 연다.
 * 문자열을 화면마다 적어 두면 서식 키를 바꿀 때 한 곳이 남는다.
 */
export const COOPERATION_FORM_KEY = 'COOPERATION';

export const FORM_SCHEMA: Record<string, FormSchema> = {
  /**
   * 일반기안 — 정형 항목이 없는 서식. 「내용」 카드를 걷으면서(2026-08-27) <b>여기에만</b> 그 칸을
   * 서식 항목으로 옮겨 왔다. 옮기지 않으면 이 서식은 제목만 남는다.
   */
  FREE: {
    hideBody: true,
    fields: [{ key: 'content', label: '내용', type: 'textarea', placeholder: '기안 내용을 적어 주세요.' }],
  },
  /**
   * 연차 사용. 결재가 완료되면 `LeaveUsageHandler` 가 **연차 원장에서 차감한다**(V76·DOC-47 §3.2-나).
   *
   * `days` 칸이 이 서식의 요점이다. 서버는 주말과 **공휴일**(`ref_holidays`, V77)을 빼고 세지만,
   * 사람이 적은 값이 있으면 그 값이 이긴다 — 공휴일 표에 없는 회사 사정(반일 근무 등)은 사람이
   * 안다. 비워 두면 서버가 센다.
   *
   * 대상자 칸은 없다. 차감 대상은 언제나 기안자 본인이고 서버가 세션에서 판정한다.
   *
   * <p>내용 칸은 <b>없다</b>({@code hideBody}). 비고 한 줄이면 충분한 문서에 자유 서술 칸을 함께
   * 두면 같은 말을 두 번 적게 되고, 그것이 연차를 내는 일을 실제보다 무겁게 만든다.
   */
  LEAVE: {
    showLeaveBalance: true,
    hideBody: true,
    fields: [
      { key: 'leaveType', label: '휴가 종류', type: 'select', options: LEAVE_TYPES, half: true },
      { key: 'startDate', label: '시작일', type: 'date', half: true },
      { key: 'endDate', label: '종료일', type: 'date', half: true },
      /*
        일수 칸은 여기 없다 — <b>잔여 연차 칸</b>이 그 자리다(사용자 지시 2026-08-27).
        「남은 연차 / 사용 연차 / 잔여 연차」 셋을 나란히 두고 가운데만 사람이 고친다. 날짜를
        고르면 자동으로 채워지고, 다르게 쓸 거면 그 자리에서 고친다 — 연휴 닷새 중 이틀만
        쓰는 경우가 흔하다. 값은 그대로 `days` 로 저장된다.
      */
      /*
        <b>「사유」가 아니라 「비고」다</b> (사용자 지시 2026-09-03: 「요즘은 사유를 쓰지 않아」).

        연차는 쓰는 사람의 권리이지 허락받는 일이 아니다. 칸 이름이 「사유」이고 「사유를 적어
        주세요」 라고 물으면, 비워 두는 것이 무언가를 감추는 일처럼 보인다 — 실제로는 대부분
        적을 것이 없다. 칸 자체는 남긴다(일정이 겹칠 때 한 줄 남길 자리는 필요하다).

        저장되는 열쇠(`reason`)는 그대로다. 서버·원장·지난 문서가 다 그 이름으로 쌓여 있고,
        바꾸면 예전 결재의 그 칸이 화면에서 사라진다 — 바뀐 것은 <b>부르는 말</b>뿐이다.
      */
      { key: 'reason', label: '비고', type: 'textarea', placeholder: '내용을 적어 주세요.' },
      /*
        대체휴무가 쓸 휴일근무(V446). 칸은 숨기고 종이의 「휴일근무 내역」 고르개({@code HolidayWorkPicker})가
        채운다. 서식 항목으로 두는 이유는 마이페이지에서 근무를 골라 연 신청서가 이 값을 들고 오게 하려는 것이다.
      */
      { key: 'holidayWorkIds', label: '휴일근무 내역', type: 'text', hidden: true },
    ],
  },
  /**
   * 시간외근무 신청 (사용자 지시 2026-10-14 · V450). `OvertimeHandler` 가 결재 완료 때 쌓는다.
   *
   * 일요일·법정공휴일은 정상 출근(하루)이다 — 승인되면 대체휴무 근거 1일이다. 평일 야근은 적은 시간이
   * 쌓여 8시간마다 「연차 전환 신청」 으로 연차 1일이 된다. 토요일도 막지 않는다 — 적은 시간이 평일처럼 쌓인다.
   *
   * 「근무 시간」 칸은 언제나 보인다(사용자 지시 2026-10-14). 일요일·법정공휴일을 고르면 8 이 채워지고
   * 고칠 수 있다({@link withOvertimeHours}).
   */
  OVERTIME: {
    hideBody: true,
    fields: [
      { key: 'workDate', label: '근무일', type: 'date', half: true },
      { key: 'hours', label: '근무 시간', type: 'number', half: true },
      { key: 'content', label: '업무 내용', type: 'textarea', placeholder: '내용을 적어 주세요.' },
    ],
  },
  /**
   * 연차 전환 신청 (V450) — 평일 시간외근무 8시간마다 연차 1일. 인사총무팀장 전결이고,
   * `OvertimeConvertHandler` 가 완료 때 연차를 부여한다. 남은 시간은 서버가 상신할 때 다시 잰다.
   */
  OVERTIME_CONVERT: {
    hideBody: true,
    fields: [
      { key: 'days', label: '전환 일수', type: 'number', half: true },
      { key: 'reason', label: '비고', type: 'textarea', placeholder: '내용을 적어 주세요.' },
      /* 쓸 시간외근무(V461). 종이의 「시간외근무 내역」 체크 목록({@code OvertimeHourPicker})이 채운다. */
      { key: 'overtimeHourIds', label: '시간외근무 내역', type: 'text', hidden: true },
    ],
  },
  /**
   * 연차 사용계획서 — 사용촉진 1차 촉구에 대한 **회신**이다(DOC-47 §3.2-라).
   *
   * 이것을 내야 2차 「사용 시기 지정 통보」 대상에서 빠진다. 날짜를 한 칸에 여러 개 받는 이유는
   * 계획이 대개 흩어진 날들이어서다 — 기간으로 받으면 "9월 중 3일" 같은 답이 오는데, 촉진에서
   * 시기를 지정하지 않은 계획은 지정한 것과 같은 효력이 없다.
   */
  LEAVE_PLAN: {
    hideBody: true,
    showLeaveBalance: true,
    fields: [
      {
        key: 'planDates',
        label: '사용 예정일',
        type: 'textarea',
        placeholder: '2026-09-14, 2026-09-15, 2026-12-24 (쉼표나 줄바꿈으로 구분)',
      },
      { key: 'daysPerDate', label: '하루당 일수', type: 'number', half: true, placeholder: '반차 계획이면 0.5' },
      { key: 'note', label: '비고', type: 'textarea' },
    ],
  },
  /**
   * 협조전 (V262 · 사용자 지시 2026-09-09 · 원본 docs/samples/협조전).
   *
   * <p>협조전은 「무엇을 협조해 달라」는 <b>글이 곧 문서</b>다 — 정형 항목으로 쪼갤 것이 없고,
   * 쪼개면 부탁하는 말이 표의 빈칸이 된다. 그래서 일반기안(FREE)과 같은 모양으로 자유 서술
   * 칸을 <b>서식 항목으로</b> 세운다(화면의 「내용」 카드는 2026-08-27 에 걷혔다).
   *
   * <p>칸이 <b>둘뿐인</b> 이유: 문서번호·시행일자는 문서가 아는 값이고, 참조는 <b>결재선의
   * 참조자</b>가 머리에 세운다({@code ApprovalDocumentView} — DOC-51 §3). 여기에 참조 칸을 또
   * 두면 종이의 「참 조 :」 줄에 두 벌의 명단이 오고, 어느 쪽이 진짜인지 알 수 없다.
   *
   * <p>받는 부서의 팀장은 결재선에 <b>합의(협조)</b>로 기안자가 직접 넣는다 — 수신이 문서마다
   * 달라 전결규정에 못 박을 수 없다(V262 머리말).
   */
  COOPERATION: {
    hideBody: true,
    recipientKey: 'recipient',
    fields: [
      { key: 'recipient', label: '수신', type: 'text', placeholder: '협조를 구하는 부서·현장 (예: 공무팀)' },
      /*
        원본 양식의 본문이 「1.」 「2.」 로 번호가 붙는 항목이라 <b>표</b>로 받는다
        (사용자 지시 2026-09-09). 자유 서술 한 칸으로 두면 번호를 사람이 직접 치게 되고,
        중간을 지운 순간 「1. 3.」 이 남는다 — 그 자국은 아무도 고치지 않는다.
      */
      { key: 'items', label: '협조 요청 사항', type: 'rows' },
    ],
  },
  PURCHASE: {
    hideBody: true,
    fields: [
      { key: 'itemName', label: '품목', type: 'text', half: true },
      { key: 'quantity', label: '수량', type: 'number', half: true },
      { key: 'unitPrice', label: '단가(원)', type: 'number', half: true },
      { key: 'vendor', label: '구매처', type: 'text', half: true },
      { key: 'reason', label: '사유', type: 'textarea' },
    ],
  },
  EXPENSE: {
    hideBody: true,
    fields: [
      { key: 'expenseItem', label: '지출 항목', type: 'text', half: true },
      { key: 'expenseAmount', label: '금액(원)', type: 'number', half: true },
      { key: 'purpose', label: '용도', type: 'textarea' },
    ],
  },
  TRIP: {
    hideBody: true,
    fields: [
      { key: 'destination', label: '출장지', type: 'text', half: true },
      { key: 'companions', label: '동행자', type: 'text', half: true },
      { key: 'startDate', label: '시작일', type: 'date', half: true },
      { key: 'endDate', label: '종료일', type: 'date', half: true },
      { key: 'purpose', label: '목적', type: 'textarea' },
    ],
  },
  /**
   * 항공권 신청 (V169 · 요청 #43).
   *
   * <p>출장(TRIP)과 <b>따로</b> 선다. 출장은 「가도 되는가」를 묻고, 항공권은 그 출장이 정해진 뒤
   * 「표를 사 달라」는 총무 업무다 — 결재선도 담당도 다르다. 합치면 국내 출장 기안자 전원이
   * 편명·좌석 칸을 지나쳐야 하고, 출장 없이 표만 끊는 경우(교육·행사)는 담을 수 없다.
   *
   * <p><b>왕복 여부를 먼저 묻는다.</b> 돌아오는 날 칸은 편도일 때 뜻이 없고, 늘 세워 두면
   * 대답할 필요 없는 칸이 하나 늘어 대충 넘겨진다(연차의 「주말도 차감할까」 와 같은 판단).
   *
   * <p>탑승자를 <b>손으로 적게</b> 둔다. 대개는 기안자 본인이지만 임원·손님 표를 총무가 대신
   * 끊는 일이 잦고, 그때 이름이 여권과 한 글자라도 다르면 발권이 되돌아온다.
   */
  FLIGHT_BOOKING: {
    hideBody: true,
    fields: [
      { key: 'passengerName', label: '탑승자', type: 'text', half: true, placeholder: '여권 영문명이 있으면 함께' },
      { key: 'tripType', label: '왕복/편도', type: 'select', options: ['왕복', '편도'], half: true },
      { key: 'departure', label: '출발지', type: 'text', half: true, placeholder: '인천 / 김포' },
      { key: 'arrival', label: '도착지', type: 'text', half: true },
      { key: 'departDate', label: '가는 날', type: 'date', half: true },
      {
        key: 'returnDate',
        label: '오는 날',
        type: 'date',
        half: true,
        // 편도에는 뜻이 없는 칸이다. 기본값이 '왕복' 이라 비어 있을 때도 보인다.
        showWhen: (formData) => (formData.tripType ?? '왕복') === '왕복',
      },
      { key: 'seatClass', label: '좌석 등급', type: 'select', options: ['이코노미', '프리미엄이코노미', '비즈니스'], half: true },
      { key: 'airline', label: '항공사·편명', type: 'text', half: true, placeholder: '정해졌으면 적어 주세요' },
      { key: 'purpose', label: '사유', type: 'textarea', placeholder: '출장·교육·행사 등. 출장 결재가 있으면 문서번호를 함께 적어 주세요.' },
    ],
  },
  /**
   * 콘도 신청 (V321 · 사용자 지시 2026-09-22).
   *
   * **콘도 이름을 고르는 칸이 아니라 적는 칸이다.** 등록된 콘도는 인사팀이 넣는 목록이고
   * (마이페이지 복지 › 콘도 예약), 그 목록을 결재 서식이 다시 읽으면 서식 칸 하나가 다른 표에
   * 묶인다 — 콘도를 내린 뒤에도 지난 문서가 그 이름을 그대로 말할 수 있어야 한다.
   *
   * 금액 칸을 열지 않는다. 요금은 콘도와 기간이 정해진 뒤 총무가 확인하는 값이고, 기안자가 적은
   * 추정 금액이 전결 구간을 가르면 배정 전에 결재선이 흔들린다.
   */
  CONDO_APPLY: {
    hideBody: true,
    fields: [
      { key: 'condoName', label: '콘도', type: 'text', half: true, placeholder: '복지 › 콘도 예약에 적힌 이름' },
      { key: 'roomType', label: '객실', type: 'text', half: true, placeholder: '아는 만큼. 총무가 확정합니다' },
      { key: 'checkIn', label: '입실일', type: 'date', half: true },
      { key: 'checkOut', label: '퇴실일', type: 'date', half: true },
      { key: 'guests', label: '인원', type: 'text', half: true, placeholder: '성인 2 · 아동 1' },
      { key: 'companion', label: '동반자', type: 'text', half: true, placeholder: '가족 관계 또는 이름' },
      { key: 'purpose', label: '사유', type: 'textarea', placeholder: '성수기·주말은 자리가 한정돼 배정 결과를 따로 알려 드립니다.' },
    ],
  },
  /**
   * 자격증 등록 신청. 결재가 완료되면 `LicenseRegisterHandler` 가 인사 자료에 등록한다.
   *
   * **대상자 칸이 없다.** 등록 대상은 언제나 기안자 본인이고, 그 판정은 서버가 세션에서 한다 —
   * 여기에 칸을 두면 남의 인사 자료에 자격증을 넣을 수 있다. 남의 것은 인사팀이 인사 화면에서 넣는다.
   *
   * 스캔본은 기안 화면의 **첨부**로 올린다. 필드로 두지 않는 이유는 첨부의 열람 권한이 결재
   * 문서에 걸려 있어서다 — 오브젝트 키를 인사 자료로 복사하면 인가가 두 벌이 된다.
   */
  LICENSE_REGISTER: {
    hideBody: true,
    fields: [
      { key: 'name', label: '자격증명', type: 'text', half: true, pick: 'qualification' },
      { key: 'issuer', label: '발급기관', type: 'text', half: true },
      { key: 'licenseNo', label: '자격증 번호', type: 'text', half: true },
      { key: 'acquiredDate', label: '취득일', type: 'date', half: true },
      { key: 'expiryDate', label: '만료일', type: 'date', half: true, placeholder: '없으면 비웁니다' },
      { key: 'note', label: '비고', type: 'textarea', placeholder: '스캔본은 첨부 칸에서 올려 주세요.' },
    ],
  },
  /**
   * 숙소 신청. 완료되면 `RelocationHandlers` 가 대장에 앉힌다(V78).
   *
   * **현장·거리·대상 여부 칸이 없다.** 셋 다 서버가 거주지(구)와 배치 현장으로 다시 계산한다 —
   * 기안에 실어 보내면 결재가 도는 며칠 사이 발령이 바뀌어도 옛 값이 남고, 무엇보다 클라이언트가
   * 보낸 값이다. 마이페이지 복지 탭이 그 판정을 먼저 보여 주므로 사람은 확인만 한다.
   */
  LODGING: {
    hideBody: true,
    fields: [
      { key: 'checkInDate', label: '입주 예정일', type: 'date', half: true },
      { key: 'checkOutDate', label: '퇴거 예정일', type: 'date', half: true, placeholder: '모르면 비웁니다' },
      { key: 'occupants', label: '입주 인원', type: 'number', half: true, placeholder: '1' },
      { key: 'roomPreference', label: '희망 사항', type: 'text', half: true, placeholder: '예: 1인실' },
      { key: 'note', label: '비고', type: 'textarea' },
    ],
  },
  /** 귀향여비 대상 신청. 월 한도는 거리 구간표(V9)가 정하고 승인 시점에 값으로 굳는다. */
  HOMECOMING: {
    hideBody: true,
    fields: [
      { key: 'note', label: '비고', type: 'textarea', placeholder: '현장·거리·한도는 서버가 판정합니다.' },
    ],
  },
  /**
   * 귀향여비 정산. 줄을 **한 칸에 여러 줄**로 받는다 — 서식의 정형 항목은 평평한 키-값이라
   * 배열을 담으려면 결재 엔진을 고쳐야 하고, 그 값은 이 서식 하나만 쓴다.
   */
  HOMECOMING_CLAIM: {
    hideBody: true,
    fields: [
      { key: 'claimMonth', label: '정산월(YYYY-MM)', type: 'text', half: true, placeholder: '2026-08' },
      {
        key: 'items',
        label: '교통비 내역',
        type: 'textarea',
        placeholder: '2026-08-15|서울→평택|버스|24000\n2026-08-17|평택→서울|버스|24000',
      },
      { key: 'note', label: '비고', type: 'textarea', placeholder: '영수증은 첨부 칸에서 올려 주세요.' },
    ],
  },
  /**
   * 경조금. **금액을 사람이 적지 않는다** — 사유를 고르면 사내 기준표의 금액과 경조휴가 일수가
   * 서버에서 붙는다(BenefitClaimHandler). 지침을 뒤져 옮겨 적으면 거기서 틀린다.
   */
  CONGRATULATION: {
    hideBody: true,
    fields: [
      { key: 'subject', label: '사유', type: 'text', half: true, placeholder: '예: 본인 결혼 · 부모 사망' },
      { key: 'eventDate', label: '해당일', type: 'date', half: true },
      { key: 'note', label: '비고', type: 'textarea', placeholder: '증빙은 첨부 칸에서 올려 주세요.' },
    ],
  },
  SCHOLARSHIP: {
    hideBody: true,
    fields: [
      { key: 'subject', label: '구분', type: 'text', half: true, placeholder: '예: 고등학교 · 대학교' },
      { key: 'eventDate', label: '학기 시작일', type: 'date', half: true },
      { key: 'childName', label: '자녀 성명', type: 'text', half: true },
      { key: 'school', label: '학교·학년', type: 'text', half: true },
      { key: 'note', label: '비고', type: 'textarea', placeholder: '등록금 증빙은 첨부 칸에서 올려 주세요.' },
    ],
  },
  /** 종합검진. 승인은 "회사가 비용을 댄다" 는 뜻이고 수검 이력은 실제로 받은 뒤 인사팀이 넣는다. */
  CHECKUP_APPLY: {
    hideBody: true,
    fields: [
      { key: 'institution', label: '희망 검진기관', type: 'text', half: true },
      { key: 'preferredDate', label: '희망 일자', type: 'date', half: true },
      { key: 'note', label: '비고', type: 'textarea' },
    ],
  },
  /**
   * 사직서 (DOC-50 §5.1). 결재가 끝나면 `ResignationHandler` 가 **퇴사일을 채우고 설문을 보낸다**.
   *
   * 두 개의 메일 주소를 받는 이유: `companyEmail` 은 **누구의 사직서인지 찾는 열쇠**이고(이름으로
   * 찾으면 동명이인이 합쳐진다), `personalEmail` 은 **설문을 받을 곳**이다 — 퇴사와 함께 사내
   * 계정이 닫히므로 사내 메일로 보내면 아무도 받지 못한다.
   *
   * 사직서의 `reasonCategory`(공식 사유)와 설문의 본심 사유를 따로 받는 것이 이 흐름의 요점이다.
   */
  RESIGNATION: {
    hideBody: true,
    fields: [
      { key: 'resignDate', label: '퇴사예정일', type: 'date', half: true },
      { key: 'resignKind', label: '퇴사 구분', type: 'select', options: ['자발', '권고', '계약만료', '정년', '기타'], half: true },
      { key: 'reasonCategory', label: '사유 구분', type: 'select', options: ['이직', '처우', '업무강도', '인간관계', '성장·경력', '건강', '가정', '통근', '기타'], half: true },
      { key: 'handoverTo', label: '인수인계자', type: 'text', half: true },
      { key: 'companyEmail', label: '사내 이메일', type: 'text', half: true, placeholder: '인사 기록을 찾는 데 씁니다' },
      { key: 'personalEmail', label: '개인 이메일', type: 'text', half: true, placeholder: '퇴사 후 설문을 받을 주소' },
      { key: 'reasonText', label: '사직 사유', type: 'textarea', placeholder: '사직서에 적히는 문구입니다.' },
      { key: 'handoverPlan', label: '인수인계 계획', type: 'textarea', placeholder: '맡고 있는 업무와 넘길 일정을 적어 주세요.' },
    ],
  },
  ASSET_PURCHASE: {
    hideBody: true,
    fields: [
      { key: 'assetType', label: '자산 종류', type: 'select', options: ['비품', '차량', '회의실'], half: true },
      { key: 'name', label: '자산명', type: 'text', half: true },
      { key: 'purchaseAmount', label: '구매금액(원)', type: 'number', half: true },
      { key: 'locationName', label: '배치(부서/현장)', type: 'text', half: true },
      { key: 'description', label: '상세/사유', type: 'textarea' },
    ],
  },

  // ── 총무 6종 (V71 · DOC-43) ────────────────────────────────────────────────
  // 칸 이름을 대장 컬럼과 **같은 이름**으로 둔다. 완료 콜백이 `formData` 를 읽어 행을 만들 때
  // 이름이 다르면 그 자리에서 매핑표가 하나 더 생기고, 칸을 하나 늘릴 때마다 두 곳을 고쳐야 한다.

  /**
   * 재직증명서 — 전자결재에서 바로 기안한 것(사용자 지시 2026-10-21). 상신하면 서버가 기안자 본인의
   * 발급 대장을 붙이고(IssuanceCompletionHandler.onSubmitted), 완료되면 직인이 찍힌 증명서가 나온다.
   * 칸 이름은 마이페이지 신청(IssuanceRequest)과 같다.
   */
  EMPLOYMENT_CERT: {
    hideBody: true,
    fields: [
      { key: 'purpose', label: '용도', type: 'text', placeholder: '은행 제출 등', half: true },
      { key: 'recipient', label: '제출처', type: 'text', half: true },
      { key: 'copies', label: '부수', type: 'number', half: true },
    ],
  },

  /**
   * 명함 제작. 완료되면 인사총무팀 취합 대장(`hr_business_card_orders`)에 앉는다.
   *
   * **오타가 이 서식의 유일한 실패 모드다.** 명함은 틀리면 다시 찍는 수밖에 없고, 대부분의 오타는
   * 사람이 자기 정보를 다시 타이핑하다 난다. 그래서 기안 화면이 인사 프로필·배치에서 채워 넣고
   * 사람은 **확인만** 한다(연차 잔여를 보여 주는 것과 같은 방식). 채움은 화면의 일이라 여기엔 칸만 있다.
   */
  BUSINESS_CARD: {
    hideBody: true,
    fields: [
      { key: 'printNameKo', label: '국문 성명', type: 'text', half: true },
      { key: 'printNameEn', label: '영문 성명', type: 'text', half: true },
      { key: 'rankName', label: '직급', type: 'text', half: true },
      { key: 'dutyName', label: '직책(표기할 때만)', type: 'text', half: true },
      { key: 'orgName', label: '소속(부서/현장)', type: 'text', half: true },
      { key: 'addressKind', label: '주소 표기', type: 'select', options: ['본사', '현장'], half: true },
      { key: 'phone', label: '휴대폰', type: 'text', half: true },
      { key: 'officePhone', label: '사무실 전화', type: 'text', half: true },
      { key: 'fax', label: '팩스', type: 'text', half: true },
      { key: 'email', label: '이메일', type: 'text', half: true },
      { key: 'quantity', label: '수량(매)', type: 'select', options: ['100', '200', '300'], half: true },
      { key: 'reason', label: '신청 사유', type: 'select', options: ['신규입사', '소진', '직급변경', '부서이동', '정보수정'], half: true },
      { key: 'receiveMethod', label: '수령 방법', type: 'select', options: ['본사 직접수령', '사내우편', '현장 발송'], half: true },
      { key: 'note', label: '비고', type: 'textarea', placeholder: '로고·직인 등 특별히 요청할 것이 있으면 적어 주세요.' },
    ],
  },

  /**
   * 승진. 완료되면 **직급이 실제로 올라간다** — 직급 이력 한 줄(`hr_rank_histories`)과
   * 인사자료의 직급, 그리고 계정의 직급이 한 트랜잭션에서 함께 간다(`PromotionHandler` · V215 §6).
   *
   * **손으로 타이핑하는 서식이 아니다.** 진급 화면(인사기록 › 경력관리 › 진급)의 「승진 상신」이
   * 대상자·현직급·승진직급을 딥링크로 채워 보낸다. 요건 판정이 이미 거기서 끝났고, 여기서 다시
   * 고르게 하면 기준표가 말한 것과 다른 직급을 적을 수 있다.
   *
   * `personnelId` 는 숨은 칸이다 — 완료 콜백이 사람을 찾는 유일한 열쇠라 이름으로 짐작하지 않는다
   * (동명이인에게 남의 승진이 붙는 사고가 그 방식에서 나온다).
   */
  PROMOTION: {
    hideBody: true,
    fields: [
      // 대상자 목록. 한 명이어도 배열이다 — 경로가 둘이면 언젠가 한쪽만 고쳐진다.
      { key: 'targets', label: '대상자 목록', type: 'text', hidden: true },
      // 사람이 읽는 명단. `targets` 는 숨은 칸이라 이것이 없으면 결재자가 «누구를 승진시키는지»
      // 모르는 문서에 서명한다. 판정에는 쓰이지 않는다(핸들러는 `targets` 만 읽는다).
      { key: 'summary', label: '대상자', type: 'textarea' },
      // 발령일은 한 장에 하나다 — 사람마다 다르면 그것은 승진 여러 장이다(발령과 같은 규칙).
      { key: 'effectiveDate', label: '발령일', type: 'date', half: true },
      { key: 'reason', label: '사유', type: 'textarea', placeholder: '비우면 「승진 결재 완료」로 직급 이력에 남습니다.' },
    ],
  },

  /**
   * 성과급 (V308 · 사용자 결정 2026-09-21). 현장이거나 부서다. 완료되면 그 건이 **확정**으로 바뀐다
   * (`SiteIncentiveHandler`) — 화면에 확정 버튼은 없다.
   *
   * **손으로 타이핑하는 서식이 아니다.** 인사자료 관리 › 성과급의 「확정 상신」이 현장·기준값·
   * 명단을 딥링크로 채워 보낸다(`incentiveDraft.ts`). 여기서 다시 적게 하면 결재 문서의 숫자와
   * 대장의 숫자가 갈리고, 그 어긋남은 확정된 뒤에 드러난다.
   *
   * `incentiveId` 는 숨은 칸이다 — 완료 콜백이 그 건을 찾는 유일한 열쇠라 현장 이름으로
   * 짐작하지 않는다(같은 이름의 현장이 둘이면 남의 정산이 확정된다).
   */
  SITE_INCENTIVE: {
    hideBody: true,
    fields: [
      { key: 'incentiveId', label: '성과급 건', type: 'text', hidden: true },
      // 현장일 수도 부서일 수도 있다(V308 · 사용자 지시 2026-09-21 — 「본사도 있을 수 있다」).
      { key: 'siteName', label: '소속(현장·부서)', type: 'text', half: true },
      { key: 'period', label: '기간', type: 'text', half: true },
      { key: 'grade', label: '평가등급', type: 'text', half: true },
      { key: 'baseHeadcount', label: '기준 인원', type: 'number', half: true },
      { key: 'budgetAmount', label: '지급금 한도(원)', type: 'number', half: true },
      // 결재 금액이 이 칸이다(`deriveAmount`). 한도가 아니라 **실제로 나가는 합계**가 전결 구간을 정한다.
      { key: 'totalAmount', label: '지급액 합계(원)', type: 'number', half: true },
      // 결재자가 읽는 명단. 숨은 칸(`incentiveId`)만으로는 «누구에게 얼마» 를 모르는 문서에 서명하게 된다.
      { key: 'summary', label: '지급 명단', type: 'textarea' },
      { key: 'reason', label: '조정 사유', type: 'textarea', placeholder: '현장안에서 고친 것이 있으면 적어 주세요.' },
    ],
  },

  /**
   * 잡자재 신청. 소모품 불출과 다르다 — 재고를 깎는 것이 아니라 **현장 원가로 나가 기성에 실린다**.
   * 그래서 `settledMonth`(기성 반영 예정월)를 기안에서 받는다. 나중에 구매팀이 대장에서 고칠 수 있다.
   */
  SITE_MATERIAL: {
    hideBody: true,
    fields: [
      { key: 'siteName', label: '현장', type: 'text', half: true },
      { key: 'workType', label: '공종', type: 'text', half: true },
      // 품목 표가 생기기 전(2026-10-29 전) 문서의 한 품목 칸. 새 기안에는 서지 않는다.
      { key: 'itemName', label: '품목', type: 'text', half: true, legacy: true },
      { key: 'spec', label: '규격', type: 'text', half: true, legacy: true },
      { key: 'quantity', label: '수량', type: 'number', half: true, legacy: true },
      { key: 'unit', label: '단위', type: 'text', half: true, legacy: true },
      { key: 'unitPrice', label: '예상 단가(원)', type: 'number', half: true, legacy: true },
      { key: 'neededBy', label: '필요일', type: 'date', half: true },
      { key: 'vendorName', label: '구매처', type: 'text', half: true },
      { key: 'settledMonth', label: '기성 반영 예정월(YYYY-MM)', type: 'text', placeholder: '2026-09', half: true },
      { key: 'items', label: '품목', type: 'items' },
      { key: 'reason', label: '사유', type: 'textarea', placeholder: '어느 공정에 쓰는지 적어 주세요.' },
    ],
  },

  /**
   * 부서경비. 금액은 **기준안 × 배치 인원**으로 화면이 산정하고(`policy_expense_standards`),
   * 사람은 확인·조정만 한다. 조정하면 사유가 필수다 — 조정분만 따로 모아 볼 수 있어야 하기 때문이다.
   */
  DEPT_EXPENSE: {
    hideBody: true,
    fields: [
      { key: 'targetMonth', label: '신청월(YYYY-MM)', type: 'text', placeholder: '2026-09', half: true },
      { key: 'orgName', label: '부서', type: 'text', half: true },
      { key: 'headCount', label: '산정 인원(자동)', type: 'number', half: true },
      { key: 'computedAmount', label: '자동 산정액(원)', type: 'number', half: true },
      { key: 'adjustedAmount', label: '조정 금액(원, 고칠 때만)', type: 'number', half: true },
      { key: 'adjustReason', label: '조정 사유', type: 'textarea', placeholder: '금액을 고쳤다면 왜 고쳤는지 적어 주세요.' },
    ],
  },

  /** 현장경비. 부서경비와 같은 모양이고 인원을 **현장 발령**에서 센다(DOC-42 배치). */
  SITE_EXPENSE: {
    hideBody: true,
    fields: [
      { key: 'targetMonth', label: '신청월(YYYY-MM)', type: 'text', placeholder: '2026-09', half: true },
      { key: 'orgName', label: '현장', type: 'text', half: true },
      { key: 'headCount', label: '산정 인원(자동)', type: 'number', half: true },
      { key: 'computedAmount', label: '자동 산정액(원)', type: 'number', half: true },
      { key: 'adjustedAmount', label: '조정 금액(원, 고칠 때만)', type: 'number', half: true },
      { key: 'adjustReason', label: '조정 사유', type: 'textarea', placeholder: '금액을 고쳤다면 왜 고쳤는지 적어 주세요.' },
    ],
  },

  /**
   * 공인인증서 신청·불출.
   *
   * **동의는 첨부가 아니라 칸이다.** 서약서를 파일로만 받으면 "이 사람이 동의했는가" 를 나중에
   * 질의할 수 없다. 고른 값과 시각을 대장(`asset_certificate_issues.consent_*`)에 값으로 남긴다.
   */
  CERT_ISSUE: {
    hideBody: true,
    fields: [
      { key: 'actionType', label: '신청 구분', type: 'select', options: ['발급', '갱신', '불출', '반납'], half: true },
      { key: 'certName', label: '인증서', type: 'text', half: true },
      { key: 'certKind', label: '종류', type: 'select', options: ['사업자', '법인', '현장', '기타'], half: true },
      { key: 'medium', label: '매체', type: 'select', options: ['USB', 'HSM', '파일', '클라우드'], half: true },
      { key: 'holderName', label: '수령자', type: 'text', half: true },
      { key: 'dueReturnAt', label: '반납 예정일', type: 'date', half: true },
      { key: 'consent', label: '사용 책임 동의', type: 'select', options: ['동의함', '동의하지 않음'], half: true },
      { key: 'purpose', label: '사용 목적', type: 'textarea', placeholder: '어느 시스템에 무엇을 하려는지 적어 주세요(전자입찰·세금계산서 등).' },
    ],
  },

  /**
   * PC Off 해제 — V243.
   *
   * 긴급 작업(10분 × 3)은 **지금 하던 것을 마치는** 시간이라 승인이 없다. 그날 밤을 켜 두려면
   * 그것으로 모자라고, 그때는 승인이 있어야 한다 — 30분을 미루는 것과 밤새 켜 두는 것은 다른
   * 결정이다(사용자 지시 2026-09-07).
   *
   * 칸은 사유 하나다. 언제까지 할 것인지는 묻지 않는다 — 승인되면 **그날은 끄지 않는다**.
   */
  PC_OFF_NIGHT: {
    hideBody: true,
    fields: [
      { key: 'reason', label: '사유', type: 'textarea', placeholder: '무슨 일로 늦게까지 하시는지 적어 주세요.' },
    ],
  },

  /**
   * 퀵서비스. 한도 이하는 **자동 승인**되고(V71 규정), 대금은 월말에 대장에서 모아 지출결의로 올린다.
   * `costOwner*` 가 비면 그 달 정산에서 부서·현장을 나눌 수 없다 — 금액보다 이 칸이 중요하다.
   */
  COURIER: {
    hideBody: true,
    fields: [
      { key: 'origin', label: '출발지', type: 'text', half: true },
      { key: 'destination', label: '도착지', type: 'text', half: true },
      { key: 'cargo', label: '물품', type: 'text', half: true },
      { key: 'vehicleType', label: '차량', type: 'select', options: ['오토바이', '다마스', '트럭', '기타'], half: true },
      { key: 'neededBy', label: '희망일', type: 'date', half: true },
      { key: 'costOwnerType', label: '비용 귀속', type: 'select', options: ['부서', '현장'], half: true },
      { key: 'costOwnerName', label: '귀속 부서/현장', type: 'text', half: true },
      { key: 'paymentType', label: '결제 구분', type: 'select', options: ['선불', '후불', '착불'], half: true },
      { key: 'estimatedAmount', label: '예상 금액(원)', type: 'number', half: true },
      { key: 'note', label: '전달 사항', type: 'textarea', placeholder: '수령인 연락처·주의사항을 적어 주세요.' },
    ],
  },

  /**
   * 기술인 교육 수강 신청 (V117 · DOC-61 §2.1).
   *
   * **금액 칸이 없다.** 수강 전이라 확정된 금액이 없고, 이 결재가 판단하는 것은 금액이 아니라
   * "이 교육을 받아야 하는가" 다. 돈 얘기는 정산 서식이 한다.
   *
   * 대상자 칸도 없다 — 대장에 줄을 만드는 대상은 언제나 기안자 본인이고, 서버가 세션에서
   * 판정한다(`TechEducationHandlers`). 칸을 두면 남의 교육 대장에 줄을 만들 수 있다.
   */
  TECH_EDU_APPLY: {
    hideBody: true,
    fields: [
      { key: 'educationType', label: '교육 구분', type: 'select', options: ['최초교육', '승급교육', '계속교육', '전문교육', '기타'], half: true },
      { key: 'institution', label: '교육기관', type: 'text', half: true },
      { key: 'courseName', label: '과정명', type: 'text' },
      { key: 'startDate', label: '교육 시작일', type: 'date', half: true },
      { key: 'endDate', label: '교육 종료일', type: 'date', half: true },
      { key: 'estimatedFee', label: '예상 교육비(원)', type: 'number', half: true, placeholder: '참고용 — 결재 금액이 아닙니다' },
      { key: 'note', label: '신청 사유', type: 'textarea', placeholder: '승급 요건·자격 유지 등 이 교육이 필요한 이유를 적어 주세요.' },
    ],
  },

  /**
   * 직무교육 수강 신청 (V314 · 사용자 지시 2026-09-21).
   *
   * 기술인 교육 수강 신청과 **칸이 같고 갈래가 다르다** — 관리책임자·안전관리자·보건관리자 교육은
   * 산업안전보건법의 직무교육이지 건설기술인 법정교육이 아니라, 완료돼도 기술인 교육 대장에는
   * 줄이 생기지 않는다. 금액·대상자 칸이 없는 이유는 `TECH_EDU_APPLY` 와 같다.
   * 세금계산서·수강신청서는 공통 첨부로 올린다.
   */
  JOB_EDU_APPLY: {
    hideBody: true,
    fields: [
      { key: 'educationType', label: '교육 구분', type: 'select', options: ['관리책임자', '안전관리자', '보건관리자', '기타'], half: true },
      { key: 'institution', label: '교육기관', type: 'text', half: true },
      { key: 'courseName', label: '과정명', type: 'text' },
      { key: 'startDate', label: '교육 시작일', type: 'date', half: true },
      { key: 'endDate', label: '교육 종료일', type: 'date', half: true },
      { key: 'estimatedFee', label: '예상 교육비(원)', type: 'number', half: true, placeholder: '참고용 — 결재 금액이 아닙니다' },
      { key: 'note', label: '신청 사유', type: 'textarea', placeholder: '선임 요건·법정 직무교육 등 이 교육이 필요한 이유를 적어 주세요.' },
    ],
  },

  /**
   * 기술인 교육비 정산 (V117 · DOC-61 §2.2).
   *
   * **`educationId` 는 화면이 채우는 칸이 아니라 앞 신청에서 이어받는 값이다.** 비어 있으면 서버가
   * 새 줄을 만든다 — 입사 전 자비로 받은 교육처럼 신청 줄이 없는 건이 실제로 있다(§2.4).
   *
   * 수료증·영수증은 **첨부**로 올린다. 여기 키 칸을 두는 이유는 결재 완료 시 대장에 옮겨 붙이기
   * 위해서이고, 화면이 첨부를 올린 뒤 그 키를 넣는다.
   */
  TECH_EDU_SETTLE: {
    hideBody: true,
    linkTo: {
      key: 'applyDocumentId',
      label: '수강 신청 문서',
      fromForms: ['TECH_EDU_APPLY'],
      hint: '고르면 교육 구분·기관·과정명이 그 신청서에서 채워지고, 대장의 같은 줄에 정산이 붙습니다.',
    },
    fields: [
      { key: 'educationType', label: '교육 구분', type: 'select', options: ['최초교육', '승급교육', '계속교육', '전문교육', '기타'], half: true },
      { key: 'institution', label: '교육기관', type: 'text', half: true },
      { key: 'courseName', label: '과정명', type: 'text' },
      { key: 'completionDate', label: '이수일', type: 'date', half: true },
      { key: 'hours', label: '이수시간', type: 'number', half: true },
      { key: 'fee', label: '교육비(원)', type: 'number', half: true },
      // 아래 넷은 사람이 치는 값이 아니다 — 선행 문서를 고르면 붙고, 파일 키는 첨부가 정한다.
      { key: 'educationId', label: '교육 대장 번호', type: 'text', hidden: true },
      { key: 'applyDocumentId', label: '수강 신청 문서', type: 'text', hidden: true },
      { key: 'certificateKey', label: '수료증 파일', type: 'text', hidden: true },
      { key: 'receiptKey', label: '영수증 파일', type: 'text', hidden: true },
      { key: 'note', label: '전달 사항', type: 'textarea' },
    ],
  },

  /**
   * 해외 현장 경비 및 기타 운영성 비용 예산 신청서 (사용자 지시 2026-11-02 · 본부장 전결).
   *
   * <p>칸이 하나다 — 엑셀 양식 그대로의 예산표({@code budget})가 본문 전부라, 「- 아 래 -」 의
   * 항목 표 대신 그 표가 선다({@link budgetSheet}). 현장 · 기간 · 통화 · 환율도 표 머리에 함께 담긴다.
   */
  [OVERSEAS_BUDGET_FORM_KEY]: {
    hideBody: true,
    fields: [{ key: 'budget', label: '예산', type: 'budget' }],
  },
};

export function schemaFor(formKey: string | null | undefined): FormSchema {
  return FORM_SCHEMA[formKey ?? 'FREE'] ?? { fields: [] };
}

/**
 * 딥링크로 받은 정형 항목을 **서식이 아는 칸만 남겨** 걸러 낸다 (DOC-20 §1.11).
 *
 * 기안 화면은 `?formKey=…&title=…&body=…` 로 값을 미리 채울 수 있고, AI 에이전트가 그 링크를
 * 만든다. 그런데 `formData` 는 백엔드까지 그대로 실려 가 **완료 콜백이 읽는 값**이라(자산 등록·
 * 자격증 등록), URL 에 적힌 것을 통째로 믿으면 서식에 없는 키가 문서에 눌러앉는다.
 *
 * 걸러 내는 것이 인가는 아니다 — 인가는 결재선과 서버가 판정한다. 여기서 막는 것은
 * **모양이 어긋난 값**이고, 그래야 화면에 보이는 칸과 저장되는 값이 같아진다.
 * 사용자는 어차피 상신 전에 화면에서 이 값을 보고 고칠 수 있다.
 */
export function pickKnownFields(formKey: string | null | undefined, raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};

  const fields = schemaFor(formKey).fields;
  const known = new Set(fields.map((f) => f.key));
  const itemKeys = new Set(fields.filter((f) => f.type === 'items').map((f) => f.key));
  const budgetKeys = new Set(fields.filter((f) => f.type === 'budget').map((f) => f.key));
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    // 품목 표는 줄의 모양까지 맞춘다 — 서식에 없는 칸이 줄 안에 실려 가지 않게.
    if (itemKeys.has(key)) {
      const items = normalizeMaterialItems(value);
      if (items.length > 0) out[key] = items;
      continue;
    }
    if (budgetKeys.has(key)) {
      const budget = normalizeBudget(value);
      if (budget) out[key] = budget;
      continue;
    }
    // 빈 값은 넣지 않는다 — 넣어 두면 "비웠다" 와 "안 채웠다" 가 구분되지 않는다.
    if (known.has(key) && value !== null && value !== undefined && value !== '') out[key] = value;
  }
  return out;
}

/** 연차 신청 일수(반차=0.5, 그 외=시작~종료 포함 일수). */
export function leaveDays(formData: Record<string, unknown>): number {
  const type = String(formData.leaveType ?? '연차');
  const given = Number(formData.days);
  if (String(formData.days ?? '') !== '' && Number.isFinite(given) && given >= 0) return given;
  // 대체휴무는 연차처럼 근무일을 센다 — 연차 잔여는 깎지 않지만, 쉬는 날 수만큼 휴일근무를 고른다.
  if (type === SUBSTITUTE_LEAVE) {
    const days = rangeOf(formData);
    return days.length === 0 ? 0 : days.length - restDaysInRange(formData).length;
  }
  // 연차가 아닌 휴가는 잔여를 깎지 않는다 — 자동값이 0이고, 사규가 다르면 사람이 올린다.
  if (!deductsAnnual(type)) return 0;
  if (type.includes('반차')) return 0.5;

  const span = rangeOf(formData);
  if (span.length === 0) return 0;
  return countsRestDays(formData) ? span.length : span.length - restDaysInRange(formData).length;
}

/**
 * 이 신청이 <b>주말·공휴일도 차감</b>하기로 한 것인가.
 *
 * <p>안 물어본 신청(평일만으로 이루어진 기간)에는 값이 없다 — 그때는 빼고 센다. 서버가 같은
 * 규칙으로 읽는다({@code LeaveUsageHandler#countsRestDays}) — 두 곳이 다르게 읽으면 화면에
 * 보이던 일수와 실제 차감이 갈린다.
 */
export function countsRestDays(formData: Record<string, unknown>): boolean {
  return String(formData.countRestDays ?? '').startsWith('포함');
}

/** 시작~종료 사이의 모든 날. 기간이 성립하지 않으면 빈 배열이다. */
function rangeOf(formData: Record<string, unknown>): Date[] {
  const start = parseDate(formData.startDate);
  const end = parseDate(formData.endDate);
  if (!start || !end || end < start) return [];

  const days: Date[] = [];
  for (const date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
    days.push(new Date(date));
  }
  return days;
}

/**
 * 그 기간에 걸린 <b>쉬는 날</b> — 토·일과 공휴일·회사 휴무일 (사용자 지시 2026-08-27).
 *
 * <p>이 목록이 비어 있지 않을 때만 화면이 「그 날들도 차감할까요」 를 묻는다. 현장은 주말에도
 * 돌아가므로 토요일 연차가 실제로 있고, 지금까지는 그 하루가 조용히 차감되지 않았다.
 */
export function restDaysInRange(formData: Record<string, unknown>): Array<{ date: Date; label: string }> {
  return rangeOf(formData)
    .map((date) => {
      const holiday = getHolidayName(date);
      if (holiday) return { date, label: holiday };
      const day = date.getDay();
      if (day === 0 || day === 6) return { date, label: day === 0 ? '일요일' : '토요일' };
      return null;
    })
    .filter((entry): entry is { date: Date; label: string } => entry !== null);
}

/** 서식이 결재 금액으로 쓸 값(구매=수량×단가, 지출=금액). 없으면 undefined. */
export function deriveAmount(formKey: string, formData: Record<string, unknown>): number | undefined {
  if (formKey === 'PURCHASE') {
    const q = Number(formData.quantity);
    const p = Number(formData.unitPrice);
    if (Number.isFinite(q) && Number.isFinite(p)) return Math.round(q * p);
  }
  if (formKey === 'EXPENSE') {
    const a = Number(formData.expenseAmount);
    if (Number.isFinite(a)) return Math.round(a);
  }
  if (formKey === 'ASSET_PURCHASE') {
    const a = Number(formData.purchaseAmount);
    if (Number.isFinite(a)) return Math.round(a);
  }
  // 잡자재는 구매와 같은 산식이다(수량 × 단가). 이 값이 전결 구간을 정한다.
  // 품목 표(DOC-139)면 줄마다 수량 × 단가의 합 — 서버 완료 콜백이 줄 금액을 같은 산식으로 앉힌다.
  if (formKey === 'SITE_MATERIAL') {
    const items = normalizeMaterialItems(formData.items);
    if (items.length > 0) return items.reduce((sum, item) => sum + (materialItemAmount(item) ?? 0), 0);
    const q = Number(formData.quantity);
    const p = Number(formData.unitPrice);
    if (Number.isFinite(q) && Number.isFinite(p)) return Math.round(q * p);
  }
  // 경비는 조정한 값이 있으면 그것이 결재 금액이다 — 자동 산정액은 근거로 남고, 올라가는 것은 조정분이다.
  // 빈 칸은 0 이 아니라 "안 채웠다" 로 본다(`Number('')` 이 0 이라 그냥 두면 조정 안 한 건이 0원으로 올라간다).
  if (formKey === 'DEPT_EXPENSE' || formKey === 'SITE_EXPENSE') {
    return filledNumber(formData.adjustedAmount) ?? filledNumber(formData.computedAmount);
  }
  if (formKey === 'COURIER') {
    return filledNumber(formData.estimatedAmount);
  }
  // 교육비 정산은 영수증 금액이 그대로 결재 금액이자 환급액이다.
  // 수강 신청(TECH_EDU_APPLY)은 금액을 올리지 않는다 — `estimatedFee` 는 참고용이라
  // 전결 구간에 손대서는 안 된다(수강 전이라 확정된 금액이 아니다).
  if (formKey === 'TECH_EDU_SETTLE') {
    return filledNumber(formData.fee);
  }
  // 성과급은 **지급액 합계**가 결재 금액이다. 한도가 아니다 — 한도는 못 넘는 선이고,
  // 전결 구간을 정하는 것은 실제로 나가는 돈이다.
  if (formKey === 'SITE_INCENTIVE') {
    return filledNumber(formData.totalAmount);
  }
  // 해외 경비 예산은 원화 합계가 결재 금액이다 — 줄마다 USD 를 원으로 접은 값의 합.
  if (formKey === OVERSEAS_BUDGET_FORM_KEY) {
    return budgetAmount(formData.budget);
  }
  return undefined;
}

/** 채워진 숫자만. 빈 문자열·null·숫자가 아닌 값은 undefined. */
function filledNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : undefined;
}

/**
 * 문서·출력에 보일 "항목: 값" 목록.
 *
 * <p><b>값이 없어도 항목은 남는다</b>(사용자 지시 2026-08-27). 빈 칸을 걸러 내면 적기 전에는
 * 표가 아예 없다가 한 칸을 채우는 순간 나타난다 — 미리보기를 열어 두고 채워 나가는 사람에게는
 * 양식이 매번 다시 그려지는 셈이다. 종이 양식은 비어 있어도 칸이 있다.
 */
export function displayFields(formKey: string | null | undefined, formData: Record<string, unknown>): Array<{ label: string; value: string }> {
  const schema = schemaFor(formKey);
  const rows = schema.fields
    // 머리의 「수 신」 줄로 가는 칸은 아래 표에 다시 세우지 않는다({@link FormSchema.recipientKey}).
    // 항목 표(`rows`)는 «항목: 값» 한 줄로 접을 수 없다 — {@link rowTables} 가 따로 그린다. 품목 표도 같다({@link itemTable}).
    .filter((f) => !f.hidden && f.type !== 'textarea' && f.type !== 'rows' && f.type !== 'items' && f.type !== 'budget' && f.key !== schema.recipientKey)
    .filter((f) => !f.legacy || showsLegacy(formData, f.key))
    // 숫자 칸은 종이에서도 쉼표를 달고 있어야 한다 — 「500000」 은 한 번 세어 보고서야 읽힌다.
    .map((f) => ({
      label: f.label,
      value: f.type === 'number' ? groupDigits(formatValue(formData[f.key])) : formatValue(formData[f.key]),
    }));
  if (schema.showLeaveBalance) {
    const days = leaveDays(formData);
    rows.push({ label: '연차 차감 일수', value: days > 0 ? `${days}일` : '' });
  }
  return rows;
}

/** 옛 칸을 그릴까 — 품목 표가 비어 있고 그 칸에 값이 적힌 옛 문서일 때만. */
export function showsLegacy(formData: Record<string, unknown>, key: string): boolean {
  if (normalizeMaterialItems(formData.items).length > 0) return false;
  const value = formData[key];
  return value !== undefined && value !== null && value !== '';
}

/** 품목 표의 줄들. 무엇이 들어와도 줄의 배열로 읽고, 품목 이름이 빈 줄은 뺀다. */
export function normalizeMaterialItems(value: unknown): MaterialItem[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
    .map((row) => {
      const text = (key: string) => (row[key] === null || row[key] === undefined ? '' : String(row[key]));
      const item: MaterialItem = {
        name: text('name'),
        spec: text('spec'),
        quantity: text('quantity'),
        unit: text('unit'),
        unitPrice: text('unitPrice'),
        note: text('note'),
      };
      if (row.provider) item.provider = text('provider');
      if (row.itemCode) item.itemCode = text('itemCode');
      if (row.productUrl) item.productUrl = text('productUrl');
      return item;
    })
    .filter((item) => item.name.trim() !== '');
}

/** 한 줄의 금액(수량 × 단가). 둘 중 하나라도 비면 null. */
export function materialItemAmount(item: MaterialItem): number | null {
  const q = ungroup(item.quantity);
  const p = ungroup(item.unitPrice);
  if (q === '' || p === '') return null;
  const amount = Number(q) * Number(p);
  // 수량은 반올림하지 않는다(2.5롤). 원 단위로 접는 것은 곱한 뒤 한 번 — 서버도 같은 자리에서 접는다.
  return Number.isFinite(amount) ? Math.round(amount) : null;
}

function ungroup(value: string): string {
  return value.replace(/,/g, '').trim();
}

/** 종이의 품목 표 — 서식에 품목 칸이 있을 때만. 옛 문서는 표가 비어 있어 null 이다. */
export function itemTable(
  formKey: string | null | undefined,
  formData: Record<string, unknown>,
): { label: string; items: MaterialItem[]; total: number } | null {
  const field = schemaFor(formKey).fields.find((f) => !f.hidden && f.type === 'items');
  if (!field) return null;
  const items = normalizeMaterialItems(formData[field.key]);
  if (items.length === 0 && showsLegacy(formData, 'itemName')) return null;
  return { label: field.label, items, total: items.reduce((sum, item) => sum + (materialItemAmount(item) ?? 0), 0) };
}

/**
 * 종이의 예산표 — 서식에 예산 칸이 있을 때만(해외 경비). 값이 비었으면 빈 표다(양식은 비어 있어도 선다).
 */
export function budgetSheet(formKey: string | null | undefined, formData: Record<string, unknown>): OverseasBudget | null {
  const field = schemaFor(formKey).fields.find((f) => !f.hidden && f.type === 'budget');
  if (!field) return null;
  return normalizeBudget(formData[field.key]) ?? normalizeBudget({})!;
}

/**
 * textarea(사유·비고 등)만 따로 — 문서에서 <b>전폭 큰 칸</b>이 된다.
 *
 * <p>여기도 빈 값을 걸러 내지 않는다. 비고 칸이 비었다고 큰 칸이 사라지면, 미리보기를 보며
 * 글을 쓰는 사람은 자기가 쓴 글이 <b>어디로 들어가는지</b>를 다 쓰고 나서야 안다.
 */
export function longFields(formKey: string | null | undefined, formData: Record<string, unknown>): Array<{ label: string; value: string }> {
  return schemaFor(formKey)
    .fields.filter((f) => !f.hidden && f.type === 'textarea')
    .map((f) => ({ label: f.label, value: formatValue(formData[f.key]) }));
}

/**
 * 문서에 <b>표로</b> 그려지는 항목 칸들 — 큰 항목 「1.」 · 하위 줄 「1)」 · 비고 (협조전).
 *
 * <p><b>아무것도 안 적었어도 표는 선다</b> (사용자 지시 2026-09-09 · {@link displayFields} 와
 * 같은 규칙). 처음에는 빈 줄을 걸러 내고 줄이 하나도 없으면 표를 세우지 않았는데, 그러면
 * 기안 화면을 처음 연 사람의 미리보기가 <b>표 없는 종이</b>다 — 무엇을 어디에 적는 문서인지
 * 다 적고 나서야 알게 된다. 종이 양식은 비어 있어도 칸이 있고, 원본 협조전도 「1) 2) 3)」이
 * 빈 줄로 인쇄돼 있다.
 *
 * <p>그래서 <b>거르지 않는다.</b> 사람이 가운데 줄을 비워 뒀으면 그 자리도 빈 줄로 남는다 —
 * 일부러 비워 둔 자리와 아직 안 적은 자리를 화면이 구분해 지울 수는 없다.
 */
export function rowTables(
  formKey: string | null | undefined,
  formData: Record<string, unknown>,
): Array<{ label: string; items: RowItem[] }> {
  return schemaFor(formKey)
    .fields.filter((f) => !f.hidden && f.type === 'rows')
    .map((f) => {
      const items = normalizeRows(formData[f.key]);
      return { label: f.label, items: items.length > 0 ? items : [{ ...EMPTY_ROW }] };
    });
}

/**
 * 무엇이 들어와도 <b>줄의 배열</b>로 읽는다.
 *
 * <p>{@code formData} 는 문자열로 저장된 JSON 이고 딥링크·옛 문서·AI 에이전트가 아무 모양이나
 * 실어 보낼 수 있다. 모양이 어긋난 값에 화면이 죽으면 그 문서는 <b>영영 열리지 않는다</b> —
 * 표 하나 때문에 결재 문서 전체를 못 보는 것보다는 빈 표가 낫다.
 *
 * <p>글자만 온 것(옛 자유 서술)은 <b>줄바꿈으로 쪼갠다</b>. 협조전이 표가 되기 전에 쌓인 문서가
 * 그 모양이라, 버리면 지난 협조전의 본문이 화면에서 사라진다.
 *
 * <p><b>빈 줄을 버리지 않는다</b>(사용자 지적 2026-09-09 — 「항목 추가 눌러도 안 늘어나는데?」).
 * 여기서 걸러 내면 [+ 항목 추가]로 만든 빈 줄이 저장되자마자 사라져 <b>표가 한 줄에서 자란 적이
 * 없는 것처럼</b> 보인다. 적을 자리를 먼저 만들고 나중에 채우는 것이 표를 쓰는 방식이다.
 * 문서에 실을 때 거르는 것은 {@link rowTables} 의 일이다 — 「고치는 중」과 「인쇄되는 것」은 다르다.
 */
export function normalizeRows(value: unknown): RowItem[] {
  if (Array.isArray(value)) {
    return value.flatMap((v) => {
      // 한때 「큰 항목」 층을 두었던 값이 남아 있을 수 있다 — 그 줄들을 «펴서» 읽는다.
      // 층을 걷어낸 뒤에도(2026-09-09) 그때 저장된 문서가 열려야 한다.
      if (v && typeof v === 'object' && Array.isArray((v as Record<string, unknown>).items)) {
        return ((v as Record<string, unknown>).items as unknown[]).map(toItem);
      }
      return [toItem(v)];
    });
  }
  if (typeof value === 'string' && value.trim() !== '') {
    return value
      .split('\n')
      .map((line) => line.replace(/^\s*\d+[.)]\s*/, '').trim())
      .filter((line) => line !== '')
      .map((content) => ({ ...EMPTY_ROW, content }));
  }
  return [];
}

function toItem(row: unknown): RowItem {
  if (typeof row === 'string') return { ...EMPTY_ROW, content: row };
  if (row && typeof row === 'object') {
    const r = row as Record<string, unknown>;
    return { content: formatValue(r.content), due: formatValue(r.due), note: formatValue(r.note) };
  }
  return { ...EMPTY_ROW };
}

/**
 * 문서 머리의 「수 신」 값. 서식이 그 칸을 정하지 않았으면 {@code null} 이고, 그때 종이에는
 * 종전대로 「내부품의」가 선다({@code ApprovalDocumentView}).
 */
export function headerRecipient(formKey: string | null | undefined, formData: Record<string, unknown>): string | null {
  const key = schemaFor(formKey).recipientKey;
  if (!key) return null;
  const value = formatValue(formData[key]).trim();
  return value || null;
}

function formatValue(value: unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'number') return value.toLocaleString('ko-KR');
  return String(value);
}

/**
 * 쉼표 붙이기·걷어내기는 <b>lib 으로 옮겼다</b>(2026-09-15) — 현장 표에도 같은 규칙이 필요해졌다.
 * 여기서 다시 내보내는 것은 이 서식 파일에서 가져다 쓰는 화면들을 건드리지 않기 위해서다.
 */
export { groupDigits, ungroupDigits } from '@/lib/money';

function parseDate(value: unknown): Date | null {
  if (typeof value !== 'string' || !value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}
